import { APP_LOGO_DATA_URI } from "./logo-data-uri";

export const APP_NAME = "EquiMare Pro";
export const APP_LOGO_URI = APP_LOGO_DATA_URI;

// ---------- Column / field definitions (mirror the mobile app) ----------
export const FIELDS = [
  { key: "left_ovary", label: "Left Ovary", placeholder: "e.g. 35mm F1" },
  { key: "right_ovary", label: "Right Ovary", placeholder: "e.g. 22mm" },
  { key: "fluid", label: "Fluid", placeholder: "e.g. 2cm" },
  { key: "edema", label: "Edema", placeholder: "e.g. 3/5" },
  { key: "cervix", label: "Cervix", placeholder: "e.g. Soft" },
  { key: "stallion", label: "Stallion", placeholder: "e.g. Omaha Beach", mareField: true },
  { key: "notes", label: "Notes", placeholder: "Add notes…", multiline: true },
  { key: "next_check", label: "Next Check", placeholder: "YYYY-MM-DD", kind: "date" },
];

export const TEASING_FIELDS = [
  { key: "result", label: "Result", placeholder: "e.g. Winking / Kicking / No response" },
  { key: "notes", label: "Notes", placeholder: "Add notes…", multiline: true },
];

export const COLUMNS = [
  { key: "left_ovary", label: "L. Ovary", printPct: 9 },
  { key: "right_ovary", label: "R. Ovary", printPct: 9 },
  { key: "fluid", label: "Fluid", printPct: 7 },
  { key: "edema", label: "Edema", printPct: 7 },
  { key: "cervix", label: "Cervix", printPct: 9 },
  { key: "stallion", label: "Stallion", printPct: 12 },
  { key: "notes", label: "Notes", printPct: 22 },
  { key: "next_check", label: "Next Check", printPct: 8 },
];

export const TEASING_COLUMNS = [
  { key: "result", label: "Result", printPct: 22 },
  { key: "notes", label: "Notes", printPct: 60 },
];

// ---------- Breeding ribbon + procedures ----------
export const PROCEDURES = [
  "Lutalyse", "Oxytocin", "Deslorelin", "Open", "Caslick", "Lavage", "Swab", "Infuse",
];

// Tap-cycle order for the colored breeding ribbon.
export const RIBBON_CYCLE = ["", "ready", "bred", "infuse"];
export const RIBBON_META = {
  "": { label: "Set breeding status", color: "transparent", border: "#C4C8D6", text: "#8A8FA3" },
  ready: { label: "Ready to breed", color: "#F2C200", border: "#C9A200", text: "#3A2F00" },
  bred: { label: "Bred", color: "#D42D2D", border: "#A81F1F", text: "#FFFFFF" },
  infuse: { label: "Post-breed infuse", color: "#2563EB", border: "#1D4FBF", text: "#FFFFFF" },
};

/** Format a single procedure tag, expanding Oxytocin with its dose detail:
 *  "Oxytocin 1ml IM x 2 (3)"  (times given, and (days)). */
export function procedureLabel(p, details) {
  if (p !== "Oxytocin") return p;
  const d = details?.Oxytocin;
  if (!d) return "Oxytocin";
  let s = "Oxytocin";
  if (d.amount) s += ` ${d.amount}ml`;
  if (d.route) s += ` ${d.route}`;
  if (d.times) s += ` x ${d.times}`;
  if (d.days) s += ` (${d.days})`;
  return s;
}

export function proceduresText(row) {
  return (row?.procedures || []).map((p) => procedureLabel(p, row?.procedure_details)).join(", ");
}

/** Notes display order: breeding label (Bred / Post-Breed Infuse) → "." →
 *  additional free-text notes → one-tap procedure tags. */
export function breedingLabel(status) {
  if (status === "bred") return "Bred";
  if (status === "infuse") return "Post-Breed Infuse";
  return "";
}

export function combinedNotes(row) {
  const label = breedingLabel(row?.breeding_status);
  let notes = (row?.notes || "").trim();
  // Strip any legacy auto-note that duplicates the derived label.
  if (["bred", "post-breed infuse", "post breed infuse"].includes(notes.toLowerCase())) notes = "";
  const procs = proceduresText(row);
  const tail = [notes, procs].filter(Boolean).join(" · ");
  if (label && tail) return `${label}. ${tail}`;
  return label || tail;
}

export const DOW_LABELS = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

// ---------- Date helpers ----------
export const toYMD = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

export const fromYMD = (s) => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};

export const addDays = (s, delta) => {
  const d = fromYMD(s);
  d.setDate(d.getDate() + delta);
  return toYMD(d);
};

export const todayYMD = () => toYMD(new Date());
export const isToday = (s) => s === todayYMD();

export const prettyDate = (s) =>
  fromYMD(s).toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });

export const prettyDateLong = (s) =>
  fromYMD(s).toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });

export const prettyDateShort = (s) => {
  if (!s) return "";
  try {
    return fromYMD(s).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return s;
  }
};

export function weekDatesFrom(s) {
  const d = fromYMD(s);
  const backToSunday = d.getDay(); // getDay(): Sun=0..Sat=6
  const sunday = new Date(d);
  sunday.setDate(d.getDate() - backToSunday);
  const out = [];
  for (let i = 0; i < 7; i++) {
    const cur = new Date(sunday);
    cur.setDate(sunday.getDate() + i);
    out.push(toYMD(cur));
  }
  return out;
}

// ---------- Settings ----------
export const DEFAULT_SETTINGS = {
  farm_name: "",
  first_name: "",
  last_name: "",
  print_name_type: "farm",
  vet_name: "",
  vet_email: "",
  owner_email: "",
  cc_recipients: "",
};

export function printedName(s) {
  if (s.print_name_type === "farm") return (s.farm_name || "").trim();
  if (s.print_name_type === "person")
    return `${s.first_name || ""} ${s.last_name || ""}`.trim();
  return "";
}

export function escapeHtml(s) {
  return String(s ?? "").replace(
    /[<>&"']/g,
    (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", '"': "&quot;", "'": "&#39;" }[c])
  );
}

/** Right-aligned practice block for the top of PDF reports (vet accounts). */
export function vetHeaderHtml(s = {}) {
  const person = escapeHtml((s.vet_practitioner_name || "").trim());
  const practice = escapeHtml((s.vet_practice_name || "").trim());
  if (!person && !practice) return "";
  return `<div class="vetbrand">${person ? `<div class="vb-name">${person}</div>` : ""}${practice ? `<div class="vb-practice">${practice}</div>` : ""}</div>`;
}

/** Open printable HTML in a hidden iframe and trigger the browser print dialog. */
export function printHtml(html) {
  // Give the print document its own <title> so the browser's print header shows
  // a branded name instead of the parent app title ("Emergent").
  const titled = /<title/i.test(html)
    ? html
    : html.replace(/<head([^>]*)>/i, "<head$1><title>EquiMare Pro</title>");
  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  document.body.appendChild(iframe);
  const doc = iframe.contentWindow.document;
  doc.open();
  doc.write(titled);
  doc.close();
  try {
    doc.title = "EquiMare Pro";
  } catch (e) {
    /* noop */
  }
  const cleanup = () => setTimeout(() => iframe.remove(), 1000);
  iframe.onload = () => {
    setTimeout(() => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } catch (e) {
        /* noop */
      }
      cleanup();
    }, 250);
  };
}

/** Trigger a browser file download for text content. */
export function downloadText(filename, content, mime = "text/csv") {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const ROWS_PER_PAGE = 18;
const WEEKLY_ROWS_PER_PAGE = 12;

const REPORT_CSS = `
  * { box-sizing: border-box; }
  @page { size: letter landscape; margin: 0.25in; }
  @page :first { margin: 0.25in; }
  html, body { -webkit-print-color-adjust:exact; print-color-adjust:exact; }
  body { font-family: 'Helvetica Neue', -apple-system, Helvetica, Arial, sans-serif; color:#000; margin:0; padding:0; background:#fff; }
  .page { page-break-inside: avoid; break-inside: avoid; }
  .page + .page { page-break-before: always; break-before: page; }
  .page-header { margin-bottom: 2px; }
  .topbrand { display:flex; align-items:flex-start; justify-content:space-between; gap:6px; margin-bottom:4px; }
  .topbrand .tb-left { display:flex; align-items:center; gap:6px; }
  .topbrand .logo { width:22px; height:22px; object-fit:contain; }
  .topbrand .brand { font-size:10px; font-weight:700; letter-spacing:1.2px; color:#03023A; text-transform:uppercase; }
  .vetbrand { text-align:right; line-height:1.15; }
  .vetbrand .vb-name { font-size:11px; font-weight:800; color:#03023A; letter-spacing:0.3px; }
  .vetbrand .vb-practice { font-size:9px; font-weight:600; color:#333; letter-spacing:0.5px; text-transform:uppercase; margin-top:1px; }
  .date-hero { text-align:center; border-bottom:2px solid #000; padding:2px 0 4px; }
  .date-hero .hero-title { font-size:15px; font-weight:800; color:#000; letter-spacing:0.8px; line-height:1.1; }
  .date-hero .hero-sub { font-size:9px; color:#333; letter-spacing:1px; margin-top:1px; }
  .meta-bar { display:flex; justify-content:space-between; align-items:center; border-bottom:1px solid #000; padding:3px 0; gap:8px; }
  .meta-bar .meta-brand { font-size:9px; font-weight:700; letter-spacing:1.8px; color:#000; }
  .meta-bar .meta-date { font-size:10px; font-weight:700; color:#000; flex:1; text-align:center; letter-spacing:0.3px; }
  .meta-bar .meta-count { font-size:9px; color:#333; }
  table { width:100%; border-collapse:collapse; font-size:11px; table-layout:fixed; border:1.2px solid #000; margin-top:4px; }
  thead tr { background:#000; color:#fff; }
  th { padding:5px 6px; text-align:left; font-weight:700; font-size:10px; text-transform:uppercase; letter-spacing:0.5px; border:1px solid #000; border-bottom:1.2px solid #000; background:#000; color:#FFF; }
  td { padding:3px 6px; border:1px solid #000; vertical-align:middle; word-wrap:break-word; line-height:1.2; font-size:11px; }
  tbody tr { page-break-inside: avoid; break-inside: avoid; }
  tbody tr:nth-child(even) td { background:#F2F2F2; }
  td.idx { width:28px; text-align:center; color:#666; font-size:9px; font-variant-numeric:tabular-nums; }
  td.mare { width:14%; font-weight:700; color:#000; background:#EDEDED !important; }
  td.notes { font-style:italic; color:#333; }
  td.next-check { font-weight:700; color:#03023A; text-align:center; font-variant-numeric:tabular-nums; letter-spacing:0.2px; }
  td.empty-row { text-align:center; padding:16px; font-style:italic; color:#666; }
  td.day-cell { vertical-align:top; padding:2px 4px; }
  td.day-cell .r { font-weight:700; color:#000; font-size:9px; }
  td.day-cell .n { font-style:italic; color:#555; font-size:8px; margin-top:1px; line-height:1.1; }
  th.day-th .dow { font-size:9px; font-weight:800; letter-spacing:0.4px; }
  th.day-th .dt { font-size:7px; font-weight:600; letter-spacing:0.3px; opacity:0.85; margin-top:1px; }
  .page-footer { display:flex; justify-content:space-between; align-items:center; margin-top:4px; padding:3px 6px; border-top:1.2px solid #000; background:#FAFAFA; font-size:8px; color:#555; letter-spacing:0.3px; }
  .page-footer .pf-brand { font-weight:700; color:#03023A; letter-spacing:1.2px; }
  .page-footer .pf-right { text-align:right; }
  .cont-section { margin-top:16px; break-inside:auto; }
  .cont-title { font-size:11px; font-weight:800; letter-spacing:1px; color:#000; text-transform:uppercase; border-bottom:1.5px solid #000; padding-bottom:3px; margin-bottom:5px; }
  .cont-title .cont-count { float:right; font-weight:600; font-size:9px; color:#333; letter-spacing:0.3px; text-transform:none; }
  tbody tr.flag-overdue td { background:#FCE3B4 !important; }
  tbody tr.flag-bred td { background:#F8D2D2 !important; }
  tbody tr.flag-confirmed td { background:#D7EFD9 !important; }
  td.chk-done { color:#AEB3BE !important; background:#F1F2F5 !important; text-decoration:line-through; font-weight:600 !important; }
  td.chk-due { color:#7A5B00 !important; background:#FFE48A !important; font-weight:800 !important; }
  td.chk-overdue { color:#8A1C0A !important; background:#F8C9C2 !important; font-weight:800 !important; }
`;

// Portrait reports: REPORT_CSS carries a landscape @page as the shared default.
// Strip it so a portrait document contains exactly one @page orientation —
// otherwise Chrome's print engine bakes in landscape and ignores manual/PDF rotation.
const stripLandscapePage = (css) => css.replace(/\s*@page\s*\{[^}]*landscape[^}]*\}/g, "");


const BREEDING_CARD_CSS = `
  @page { size: letter portrait; margin: 0.35in; }
  @page :first { margin: 0.35in; }
  .horse-info { margin-top: 6px; margin-bottom: 6px; border: 1.2px solid #000; background: #FAFAFA; padding: 6px 8px; display: grid; grid-template-columns: 1fr 1fr; column-gap: 14px; row-gap: 3px; }
  .horse-info .hi-row { display:flex; align-items:baseline; gap:6px; font-size:10px; line-height:1.25; }
  .horse-info .hi-label { font-weight:800; letter-spacing:0.4px; text-transform:uppercase; color:#000; font-size:9px; min-width:118px; }
  .horse-info .hi-val { flex:1; color:#000; font-weight:600; border-bottom:1px dotted #999; padding-bottom:1px; min-height:12px; }
  .horse-info .hi-val.empty { color:#999; font-style:italic; font-weight:500; }
  table.card td.card-date { width:16%; font-weight:800; color:#000; background:#EDEDED !important; text-align:center; font-variant-numeric:tabular-nums; letter-spacing:0.2px; }
  table.card thead th { font-size:10px; }
`;

function renderPageSection(opts) {
  return `
    <section class="page">
      <div class="page-header">
        <div class="topbrand">
          <div class="tb-left">
            <img class="logo" src="${APP_LOGO_URI}" alt="logo"/>
            <div class="brand">EquiMare Pro</div>
          </div>
          ${opts.vetHeader || ""}
        </div>
        <div class="date-hero">
          <div class="hero-title">${opts.heroTitle}</div>
          ${opts.heroSub ? `<div class="hero-sub">${opts.heroSub}</div>` : ""}
        </div>
        <div class="meta-bar">
          <div class="meta-brand">&nbsp;</div>
          <div class="meta-date">${opts.dateBanner}</div>
          <div class="meta-count">${opts.metaCount}</div>
        </div>
      </div>
      <table>
        ${opts.colgroupHtml}
        <thead>${opts.columnHeaderTr}</thead>
        <tbody>${opts.bodyRowsHtml}</tbody>
      </table>
      ${opts.appendHtml || ""}
      <div class="page-footer">
        <div class="pf-left">${opts.footerBrandStrip}</div>
        <div class="pf-right">${opts.footerRight}</div>
      </div>
    </section>`;
}

const FOAL_REPORT_COLS = [
  { key: "physical_exam", label: "Physical Exam", printPct: 14 },
  { key: "umbilicus", label: "Umbilicus", printPct: 10 },
  { key: "medication", label: "Medication", printPct: 13 },
  { key: "bandages", label: "Bandages", printPct: 10 },
  { key: "notes", label: "Notes", printPct: 19 },
  { key: "additional_procedures", label: "Add. Procedures", printPct: 16 },
  { key: "next_check", label: "Next Check", printPct: 8 },
];

function buildFoalContinuation(foalRows, settings) {
  if (!foalRows || foalRows.length === 0) return "";
  const cols = FOAL_REPORT_COLS;
  const filled = foalRows.filter((r) => cols.some((c) => r[c.key])).length;

  const headerCells = cols.map((c) => `<th>${c.label.toUpperCase()}</th>`).join("");
  const columnHeaderTr = `<tr><th>#</th><th>Foal (Dam)</th>${headerCells}</tr>`;

  const dataPctSum = cols.reduce((s, c) => s + (c.printPct ?? 0), 0);
  const reservedIdx = 3;
  const reservedMare = 12;
  const slack = Math.max(0, 100 - reservedIdx - reservedMare - dataPctSum);
  const perColBoost = cols.length ? slack / cols.length : 0;
  const colgroupHtml =
    `<colgroup>` +
    `<col style="width:${reservedIdx}%"/>` +
    `<col style="width:${reservedMare}%"/>` +
    cols.map((c) => `<col style="width:${((c.printPct ?? 0) + perColBoost).toFixed(2)}%"/>`).join("") +
    `</colgroup>`;

  const cellsFor = (r) =>
    cols
      .map((c) => {
        let raw = r[c.key] ?? "";
        if (c.key === "next_check" && raw) raw = prettyDateShort(raw);
        const val = escapeHtml(raw) || "";
        if (c.key === "notes" || c.key === "additional_procedures") return `<td class="notes">${val}</td>`;
        if (c.key === "next_check") return `<td class="next-check">${val}</td>`;
        return `<td>${val}</td>`;
      })
      .join("");

  const bodyRowsHtml = foalRows
    .map((r, i) => `<tr><td class="idx">${i + 1}</td><td class="mare">${escapeHtml(foalName(r))}</td>${cellsFor(r)}</tr>`)
    .join("");

  return `
    <div class="cont-section">
      <div class="cont-title">DAILY FOAL ROUNDS <span class="cont-count">${foalRows.length} foal${foalRows.length === 1 ? "" : "s"} &middot; ${filled} with entries</span></div>
      <table>
        ${colgroupHtml}
        <thead>${columnHeaderTr}</thead>
        <tbody>${bodyRowsHtml}</tbody>
      </table>
    </div>`;
}

export function buildReportHtml(date, rows, settings = DEFAULT_SETTINGS, kind = "daily", foalRows = []) {
  const cols = kind === "teasing" ? TEASING_COLUMNS : COLUMNS;
  const stats = rows.reduce(
    (acc, r) => {
      if (cols.some((c) => r[c.key])) acc.filled += 1;
      return acc;
    },
    { filled: 0 }
  );
  const nameHero = escapeHtml(printedName(settings));
  const kindLabel = kind === "teasing" ? "Teasing Report" : "Daily Mare Report";
  const heroTitle = (nameHero || kindLabel).toUpperCase();
  const heroSub = nameHero ? kindLabel.toUpperCase() : "";
  const longDate = prettyDateLong(date);
  const generatedAt = new Date().toLocaleString();

  const headerCells = cols.map((c) => `<th>${c.label.toUpperCase()}</th>`).join("");
  const cellsFor = (r) =>
    cols
      .map((c) => {
        let raw = r[c.key] ?? "";
        if (c.key === "next_check" && raw) raw = prettyDateShort(raw);
        if (c.key === "notes") {
          const val = escapeHtml(combinedNotes(r)) || "";
          return `<td class="notes">${val}</td>`;
        }
        const val = escapeHtml(raw) || "";
        if (c.key === "next_check") return `<td class="next-check">${val}</td>`;
        return `<td>${val}</td>`;
      })
      .join("");

  const chunkSize = ROWS_PER_PAGE;
  const chunks =
    rows.length === 0
      ? [[]]
      : Array.from({ length: Math.ceil(rows.length / chunkSize) }, (_, i) =>
          rows.slice(i * chunkSize, i * chunkSize + chunkSize)
        );
  const totalPages = chunks.length;

  const dataPctSum = cols.reduce((s, c) => s + (c.printPct ?? 0), 0);
  const reservedIdx = 3;
  const reservedMare = kind === "teasing" ? 15 : 9;
  const slack = Math.max(0, 100 - reservedIdx - reservedMare - dataPctSum);
  const perColBoost = cols.length ? slack / cols.length : 0;
  const colgroupHtml =
    `<colgroup>` +
    `<col style="width:${reservedIdx}%"/>` +
    `<col style="width:${reservedMare}%"/>` +
    cols.map((c) => `<col style="width:${((c.printPct ?? 0) + perColBoost).toFixed(2)}%"/>`).join("") +
    `</colgroup>`;

  const columnHeaderTr = `<tr><th>#</th><th>Mare</th>${headerCells}</tr>`;
  const renderRow = (r, absIdx) =>
    `<tr><td class="idx">${absIdx + 1}</td><td class="mare">${escapeHtml(r.mare_name)}</td>${cellsFor(r)}</tr>`;

  const foalContinuation = kind === "daily" ? buildFoalContinuation(foalRows, settings) : "";

  const pagesHtml = chunks
    .map((chunk, pageIdx) => {
      const startAbsIdx = pageIdx * chunkSize;
      const bodyRowsHtml =
        chunk.length === 0
          ? `<tr><td colspan="${cols.length + 2}" class="empty-row">No records for this day.</td></tr>`
          : chunk.map((r, i) => renderRow(r, startAbsIdx + i)).join("");
      return renderPageSection({
        heroTitle,
        heroSub,
        vetHeader: vetHeaderHtml(settings),
        dateBanner: longDate.toUpperCase(),
        metaCount: `${rows.length} row${rows.length === 1 ? "" : "s"} &middot; ${stats.filled} with entries`,
        colgroupHtml,
        columnHeaderTr,
        bodyRowsHtml,
        appendHtml: pageIdx === totalPages - 1 ? foalContinuation : "",
        footerBrandStrip: `<span class="pf-brand">EQUIMARE PRO</span> &middot; ${kindLabel} &middot; ${longDate}`,
        footerRight: `Page ${pageIdx + 1} of ${totalPages} &middot; Generated ${generatedAt}`,
      });
    })
    .join("");

  return `<!doctype html><html><head><meta charset="utf-8"/><style>${REPORT_CSS}</style></head><body>${pagesHtml}</body></html>`;
}

/** Print multiple Daily Rounds charts across a date range (one day per page group). */
export function buildDailyRangeHtml(days, settings = DEFAULT_SETTINGS) {
  const bodies = (days || [])
    .map((d) => {
      const doc = buildReportHtml(d.date, d.rows || [], settings, "daily", d.foalRows || []);
      const m = doc.match(/<body>([\s\S]*)<\/body>/);
      return m ? m[1] : "";
    })
    .filter(Boolean)
    .join('<div style="break-before: page; page-break-before: always;"></div>');
  return `<!doctype html><html><head><meta charset="utf-8"/><style>${REPORT_CSS}</style></head><body>${bodies}</body></html>`;
}

export function buildWeeklyTeasingReportHtml(week, settings = DEFAULT_SETTINGS) {
  const { start, end, dates, rows } = week;
  const filled = rows.reduce(
    (acc, r) => acc + (dates.some((d) => r.days[d]?.result || r.days[d]?.notes) ? 1 : 0),
    0
  );
  const nameHero = escapeHtml(printedName(settings));
  const kindLabel = "Weekly Teasing Chart";
  const heroTitle = (nameHero || kindLabel).toUpperCase();
  const heroSub = nameHero ? kindLabel.toUpperCase() : "";
  const weekBanner = `${prettyDate(start)} — ${prettyDate(end)}`.toUpperCase();
  const generatedAt = new Date().toLocaleString();

  const dayHeaderCells = dates
    .map((d, i) => {
      const wd = fromYMD(d);
      const dt = wd.toLocaleDateString(undefined, { month: "short", day: "numeric" });
      return `<th class="day-th"><div class="dow">${DOW_LABELS[i]}</div><div class="dt">${escapeHtml(dt).toUpperCase()}</div></th>`;
    })
    .join("");
  const columnHeaderTr = `<tr><th>#</th><th>Mare</th>${dayHeaderCells}</tr>`;
  const dayPct = ((100 - 3 - 15) / dates.length).toFixed(2);
  const colgroupHtml =
    `<colgroup><col style="width:3%"/><col style="width:15%"/>` +
    dates.map(() => `<col style="width:${dayPct}%"/>`).join("") +
    `</colgroup>`;

  const renderRow = (r, absIdx) => {
    const dayCells = dates
      .map((d) => {
        const c = r.days[d] || { result: "", notes: "" };
        const rv = escapeHtml(c.result || "");
        const nv = escapeHtml(c.notes || "");
        return `<td class="day-cell">${rv ? `<div class="r">${rv}</div>` : ""}${nv ? `<div class="n">${nv}</div>` : ""}</td>`;
      })
      .join("");
    return `<tr><td class="idx">${absIdx + 1}</td><td class="mare">${escapeHtml(r.mare_name)}</td>${dayCells}</tr>`;
  };

  const chunkSize = WEEKLY_ROWS_PER_PAGE;
  const chunks =
    rows.length === 0
      ? [[]]
      : Array.from({ length: Math.ceil(rows.length / chunkSize) }, (_, i) =>
          rows.slice(i * chunkSize, i * chunkSize + chunkSize)
        );
  const totalPages = chunks.length;

  const pagesHtml = chunks
    .map((chunk, pageIdx) => {
      const startAbsIdx = pageIdx * chunkSize;
      const bodyRowsHtml =
        chunk.length === 0
          ? `<tr><td colspan="9" class="empty-row">No teasing records for this week.</td></tr>`
          : chunk.map((r, i) => renderRow(r, startAbsIdx + i)).join("");
      return renderPageSection({
        heroTitle,
        heroSub,
        vetHeader: vetHeaderHtml(settings),
        dateBanner: weekBanner,
        metaCount: `${rows.length} mare${rows.length === 1 ? "" : "s"} &middot; ${filled} with entries`,
        colgroupHtml,
        columnHeaderTr,
        bodyRowsHtml,
        footerBrandStrip: `<span class="pf-brand">EQUIMARE PRO</span> &middot; ${kindLabel} &middot; ${prettyDate(start)}—${prettyDate(end)}`,
        footerRight: `Page ${pageIdx + 1} of ${totalPages} &middot; Generated ${generatedAt}`,
      });
    })
    .join("");

  return `<!doctype html><html><head><meta charset="utf-8"/><style>${REPORT_CSS}</style></head><body>${pagesHtml}</body></html>`;
}

const BC_COLS = [
  { key: "date", label: "Date", pct: 12 },
  { key: "left_ovary", label: "L. Ovary", pct: 11 },
  { key: "right_ovary", label: "R. Ovary", pct: 11 },
  { key: "fluid", label: "Fluid", pct: 8 },
  { key: "edema", label: "Edema", pct: 8 },
  { key: "cervix", label: "Cervix", pct: 10 },
  { key: "notes", label: "Notes", pct: 30 },
  { key: "next_check", label: "Next Check", pct: 10 },
];

function bcColgroup() {
  return `<colgroup>${BC_COLS.map((c) => `<col style="width:${c.pct}%"/>`).join("")}</colgroup>`;
}
function bcHeaderTr() {
  return `<tr>${BC_COLS.map((c) => `<th>${c.label.toUpperCase()}</th>`).join("")}</tr>`;
}
function bcRenderRow(r) {
  return `<tr>${BC_COLS.map((c) => {
    let raw = r[c.key] ?? "";
    if ((c.key === "date" || c.key === "next_check") && raw) raw = prettyDateShort(raw);
    if (c.key === "notes") return `<td class="notes">${escapeHtml(combinedNotes(r))}</td>`;
    const val = escapeHtml(raw) || "";
    if (c.key === "date") return `<td class="card-date">${val}</td>`;
    if (c.key === "next_check") return `<td class="next-check">${val}</td>`;
    return `<td>${val}</td>`;
  }).join("")}</tr>`;
}
function horseInfoBlock(mare, farmName) {
  const row = (label, value) => {
    const v = (value || "").trim();
    const shown = v && /^\d{4}-\d{2}-\d{2}$/.test(v) ? prettyDate(v) : v;
    return `<div class="hi-row"><div class="hi-label">${label}</div><div class="hi-val ${shown ? "" : "empty"}">${shown ? escapeHtml(shown) : "—"}</div></div>`;
  };
  return `<div class="horse-info">
      ${row("Farm Name", farmName)}
      ${row("Stallion", mare.stallion || "")}
      ${row("Horse", mare.name)}
      ${row("Last Date Serviced", mare.last_service_date || "")}
      ${row("Confirmed in Foal", mare.confirmed_in_foal_date || "")}
      ${row("Owner", mare.owner || "")}
      ${row("Estimated Due Date", mare.estimated_due_date || "")}
      ${row("Foal Out Date", mare.foal_out_date || "")}
      <div class="hi-row"><div class="hi-label">&nbsp;</div><div class="hi-val" style="border:none">&nbsp;</div></div>
    </div>`;
}

const FIRST_PAGE_ROWS = 22;
const CONT_PAGE_ROWS = 30;

function chunkRecords(records) {
  const chunks = [];
  if (records.length === 0) return [[]];
  let i = 0;
  let isFirst = true;
  while (i < records.length) {
    const size = isFirst ? FIRST_PAGE_ROWS : CONT_PAGE_ROWS;
    chunks.push(records.slice(i, i + size));
    i += size;
    isFirst = false;
  }
  return chunks;
}

function statsFor(records) {
  return {
    total: records.length,
    filled: records.filter((r) =>
      [r.left_ovary, r.right_ovary, r.fluid, r.edema, r.cervix, r.notes, r.next_check].some(
        (v) => (v || "").trim().length > 0
      )
    ).length,
  };
}

export function buildBreedingCardHtml(mare, history, settings = DEFAULT_SETTINGS) {
  const farmName = (settings.farm_name || "").trim() || printedName(settings) || "";
  const heroSub = `${(mare.name || "").toUpperCase()}${farmName ? ` &middot; ${escapeHtml(farmName.toUpperCase())}` : ""}`;
  const dateBanner = `${history.year} SEASON`;
  const generatedAt = new Date().toLocaleString();
  const sorted = [...history.records].sort((a, b) => a.date.localeCompare(b.date));
  const stats = statsFor(sorted);
  const chunks = chunkRecords(sorted);
  const totalPages = chunks.length;
  const horseInfoHtml = horseInfoBlock(mare, farmName);

  const pagesHtml = chunks
    .map((chunk, pageIdx) => {
      const bodyRowsHtml =
        chunk.length === 0
          ? `<tr><td colspan="${BC_COLS.length}" class="empty-row">No records for ${history.year} yet — add ${escapeHtml(mare.name)} to a day and start tracking.</td></tr>`
          : chunk.map((r) => bcRenderRow(r)).join("");
      return `<section class="page">
      <div class="page-header">
        <div class="topbrand"><div class="tb-left"><img class="logo" src="${APP_LOGO_URI}" alt="logo"/><div class="brand">EquiMare Pro</div></div>${vetHeaderHtml(settings)}</div>
        <div class="date-hero"><div class="hero-title">BREEDING CARD</div><div class="hero-sub">${heroSub}</div></div>
        <div class="meta-bar"><div class="meta-brand">&nbsp;</div><div class="meta-date">${dateBanner}</div><div class="meta-count">${stats.total} record${stats.total === 1 ? "" : "s"} &middot; ${stats.filled} with entries</div></div>
      </div>
      ${pageIdx === 0 ? horseInfoHtml : ""}
      <table class="card">${bcColgroup()}<thead>${bcHeaderTr()}</thead><tbody>${bodyRowsHtml}</tbody></table>
      <div class="page-footer"><div class="pf-left"><span class="pf-brand">EQUIMARE PRO</span> &middot; Breeding Card &middot; ${escapeHtml(mare.name)} &middot; ${history.year}</div><div class="pf-right">Page ${pageIdx + 1} of ${totalPages} &middot; Generated ${generatedAt}</div></div>
    </section>`;
    })
    .join("");

  return `<!doctype html><html><head><meta charset="utf-8"/><style>${stripLandscapePage(REPORT_CSS)}${BREEDING_CARD_CSS}</style></head><body>${pagesHtml}</body></html>`;
}

export function buildBulkBreedingCardsHtml(cards, settings = DEFAULT_SETTINGS, year) {
  const shownYear = year ?? cards[0]?.history?.year ?? new Date().getFullYear();
  const generatedAt = new Date().toLocaleString();
  const farmName = (settings.farm_name || "").trim() || printedName(settings) || "";

  if (!cards || cards.length === 0) {
    return `<!doctype html><html><head><meta charset="utf-8"/><style>${stripLandscapePage(REPORT_CSS)}${BREEDING_CARD_CSS}</style></head><body>
    <section class="page"><div class="page-header"><div class="topbrand"><div class="tb-left"><img class="logo" src="${APP_LOGO_URI}" alt="logo"/><div class="brand">EquiMare Pro</div></div>${vetHeaderHtml(settings)}</div>
    <div class="date-hero"><div class="hero-title">BULK BREEDING CARDS</div><div class="hero-sub">${escapeHtml((farmName || "").toUpperCase())}</div></div>
    <div class="meta-bar"><div class="meta-brand">&nbsp;</div><div class="meta-date">${shownYear} SEASON</div><div class="meta-count">0 mares with entries</div></div></div>
    <table><tbody><tr><td class="empty-row">No mares have any records for ${shownYear}. Add records first and try again.</td></tr></tbody></table>
    <div class="page-footer"><div class="pf-left"><span class="pf-brand">EQUIMARE PRO</span> &middot; Bulk Breeding Cards &middot; ${shownYear}</div><div class="pf-right">Generated ${generatedAt}</div></div></section></body></html>`;
  }

  const cardPageCounts = cards.map(({ history }) => Math.max(1, chunkRecords([...history.records]).length));
  const grandTotalPages = cardPageCounts.reduce((s, n) => s + n, 0);

  let cursor = 0;
  const allPagesHtml = cards
    .map(({ mare, history }, cardIdx) => {
      const sorted = [...history.records].sort((a, b) => a.date.localeCompare(b.date));
      const stats = statsFor(sorted);
      const chunks = chunkRecords(sorted);
      const heroSub = `${(mare.name || "").toUpperCase()}${farmName ? ` &middot; ${escapeHtml(farmName.toUpperCase())}` : ""}`;
      const horseInfoHtml = horseInfoBlock(mare, farmName);
      return chunks
        .map((chunk, localPageIdx) => {
          cursor += 1;
          const bodyRowsHtml =
            chunk.length === 0
              ? `<tr><td colspan="${BC_COLS.length}" class="empty-row">No records for ${history.year}.</td></tr>`
              : chunk.map((r) => bcRenderRow(r)).join("");
          return `<section class="page">
      <div class="page-header">
        <div class="topbrand"><div class="tb-left"><img class="logo" src="${APP_LOGO_URI}" alt="logo"/><div class="brand">EquiMare Pro</div></div>${vetHeaderHtml(settings)}</div>
        <div class="date-hero"><div class="hero-title">BREEDING CARD</div><div class="hero-sub">${heroSub}</div></div>
        <div class="meta-bar"><div class="meta-brand">Card ${cardIdx + 1} of ${cards.length}</div><div class="meta-date">${history.year} SEASON</div><div class="meta-count">${stats.total} record${stats.total === 1 ? "" : "s"} &middot; ${stats.filled} with entries</div></div>
      </div>
      ${localPageIdx === 0 ? horseInfoHtml : ""}
      <table class="card">${bcColgroup()}<thead>${bcHeaderTr()}</thead><tbody>${bodyRowsHtml}</tbody></table>
      <div class="page-footer"><div class="pf-left"><span class="pf-brand">EQUIMARE PRO</span> &middot; Bulk Breeding Cards &middot; ${escapeHtml(mare.name)} &middot; ${history.year}</div><div class="pf-right">Page ${cursor} of ${grandTotalPages} &middot; Generated ${generatedAt}</div></div>
    </section>`;
        })
        .join("");
    })
    .join("");

  return `<!doctype html><html><head><meta charset="utf-8"/><style>${stripLandscapePage(REPORT_CSS)}${BREEDING_CARD_CSS}</style></head><body>${allPagesHtml}</body></html>`;
}

// ---------- Foal daily rounds ----------
export function foalName(foal) {
  if (!foal) return "Foal";
  const dam = (foal.mare_name || "Foal").trim();
  const yy = (foal.foal_out_date || "").slice(2, 4); // YYYY-MM-DD -> YY
  return yy ? `${dam} ${yy}` : dam;
}

export const FOAL_DAILY_FIELDS = [
  { key: "physical_exam", label: "Physical Exam", placeholder: "e.g. Bright, alert, responsive" },
  { key: "umbilicus", label: "Umbilicus", placeholder: "e.g. Clean & dry" },
  { key: "medication", label: "Medication", placeholder: "e.g. —" },
  { key: "bandages", label: "Bandages", placeholder: "e.g. —" },
  { key: "notes", label: "Notes", placeholder: "Add notes…", multiline: true },
  { key: "additional_procedures", label: "Additional Procedures", placeholder: "Add procedures…", multiline: true },
  { key: "farrier_notes", label: "Farrier Notes", placeholder: "Trim / shoeing / observations…", multiline: true },
  { key: "farrier_next_due", label: "Farrier Next Due", placeholder: "YYYY-MM-DD", kind: "date" },
  { key: "next_check", label: "Next Check", placeholder: "YYYY-MM-DD", kind: "date" },
];

export const FOAL_DAILY_COLUMNS = [
  { key: "physical_exam", label: "Physical Exam" },
  { key: "umbilicus", label: "Umbilicus" },
  { key: "medication", label: "Medication" },
  { key: "bandages", label: "Bandages" },
  { key: "notes", label: "Notes" },
  { key: "additional_procedures", label: "Add. Procedures" },
  { key: "next_check", label: "Next Check" },
];

export const FOAL_FARRIER_COLUMNS = [
  { key: "notes", label: "Farrier Notes" },
  { key: "next_due", label: "Farrier Next Due" },
];

const FOAL_CARD_CSS = `
  .foal-info { margin-top: 6px; margin-bottom: 6px; border: 1.2px solid #000; background: #FAFAFA; padding: 6px 8px; display: grid; grid-template-columns: 1fr 1fr; column-gap: 14px; row-gap: 3px; }
  .foal-info .fi-row { display:flex; align-items:baseline; gap:6px; font-size:10px; line-height:1.25; }
  .foal-info .fi-label { font-weight:800; letter-spacing:0.4px; text-transform:uppercase; color:#000; font-size:9px; min-width:110px; }
  .foal-info .fi-val { flex:1; color:#000; font-weight:600; border-bottom:1px dotted #999; padding-bottom:1px; min-height:12px; }
  .foal-info .fi-val.empty { color:#999; font-style:italic; font-weight:500; }
`;

const FOAL_BC_COLS = [
  { key: "date", label: "Date", pct: 9 },
  { key: "physical_exam", label: "Physical Exam", pct: 15 },
  { key: "umbilicus", label: "Umbilicus", pct: 11 },
  { key: "medication", label: "Medication", pct: 14 },
  { key: "bandages", label: "Bandages", pct: 11 },
  { key: "notes", label: "Notes", pct: 16 },
  { key: "additional_procedures", label: "Add. Procedures", pct: 15 },
  { key: "next_check", label: "Next Check", pct: 9 },
];

function foalInfoBlock(foal, farmName) {
  const row = (label, value) => {
    const v = (value || "").trim();
    const shown = v && /^\d{4}-\d{2}-\d{2}$/.test(v) ? prettyDate(v) : v;
    return `<div class="fi-row"><div class="fi-label">${label}</div><div class="fi-val ${shown ? "" : "empty"}">${shown ? escapeHtml(shown) : "—"}</div></div>`;
  };
  return `<div class="foal-info">
      ${row("Farm Name", farmName)}
      ${row("Dam (Mare)", foal.mare_name || "")}
      ${row("Stallion", foal.stallion || "")}
      ${row("Foal Out Date", foal.foal_out_date || "")}
      ${row("Sex", foal.sex || "")}
      ${row("Color", foal.color || "")}
      ${row("Breed", foal.breed || "")}
      <div class="fi-row"><div class="fi-label">&nbsp;</div><div class="fi-val" style="border:none">&nbsp;</div></div>
    </div>`;
}

export function buildFoalCardHtml(foal, records, farrier = [], settings = DEFAULT_SETTINGS) {
  const farmName = (foal.farm || "").trim() || (settings.farm_name || "").trim() || printedName(settings) || "";
  // Records ASCENDING for print.
  const sorted = [...(records || [])].sort((a, b) => (a.date || "").localeCompare(b.date || ""));
  const filled = sorted.filter((r) =>
    FOAL_DAILY_COLUMNS.some((c) => (r[c.key] || "").trim())
  ).length;
  const heroSub = `${escapeHtml(foalName(foal).toUpperCase())}${farmName ? ` &middot; ${escapeHtml(farmName.toUpperCase())}` : ""}`;
  const generatedAt = new Date().toLocaleString();

  const colgroup = `<colgroup>${FOAL_BC_COLS.map((c) => `<col style="width:${c.pct}%"/>`).join("")}</colgroup>`;
  const headerTr = `<tr>${FOAL_BC_COLS.map((c) => `<th>${c.label.toUpperCase()}</th>`).join("")}</tr>`;
  const renderRow = (r) =>
    `<tr>${FOAL_BC_COLS.map((c) => {
      let raw = r[c.key] ?? "";
      if ((c.key === "date" || c.key === "next_check") && raw) raw = prettyDateShort(raw);
      const val = escapeHtml(raw) || "";
      if (c.key === "date") return `<td class="mare" style="text-align:center">${val}</td>`;
      if (c.key === "notes" || c.key === "additional_procedures") return `<td class="notes">${val}</td>`;
      if (c.key === "next_check") return `<td class="next-check">${val}</td>`;
      return `<td>${val}</td>`;
    }).join("")}</tr>`;

  const bodyRows =
    sorted.length === 0
      ? `<tr><td colspan="${FOAL_BC_COLS.length}" class="empty-row">No exam records yet — add this foal to Daily Rounds to start tracking.</td></tr>`
      : sorted.map((r) => renderRow(r)).join("");

  // Farrier visits (ASC for print), only rows that have notes or a next-due.
  const farSorted = [...(farrier || [])]
    .filter((r) => (r.notes || "").trim() || (r.next_due || "").trim())
    .sort((a, b) => (a.date || "").localeCompare(b.date || ""));
  const farrierBlock = farSorted.length
    ? `<table style="margin-top:10px"><colgroup><col style="width:14%"/><col style="width:56%"/><col style="width:30%"/></colgroup>
        <thead><tr><th>DATE</th><th>FARRIER NOTES</th><th>NEXT DUE</th></tr></thead>
        <tbody>${farSorted
          .map(
            (r) =>
              `<tr><td class="mare" style="text-align:center">${escapeHtml(prettyDateShort(r.date)) || ""}</td><td class="notes">${escapeHtml(r.notes || "") || ""}</td><td class="next-check">${r.next_due ? escapeHtml(prettyDateShort(r.next_due)) : ""}</td></tr>`
          )
          .join("")}</tbody></table>`
    : "";
  const farrierSection = farrierBlock
    ? `<div class="date-hero" style="margin-top:12px"><div class="hero-title" style="font-size:13px">FARRIER RECORD</div></div>${farrierBlock}`
    : "";

  const pageHtml = `<section class="page">
      <div class="page-header">
        <div class="topbrand"><div class="tb-left"><img class="logo" src="${APP_LOGO_URI}" alt="logo"/><div class="brand">EquiMare Pro</div></div>${vetHeaderHtml(settings)}</div>
        <div class="date-hero"><div class="hero-title">FOAL CARD</div><div class="hero-sub">${heroSub}</div></div>
        <div class="meta-bar"><div class="meta-brand">&nbsp;</div><div class="meta-date">FOAL RECORD</div><div class="meta-count">${sorted.length} exam${sorted.length === 1 ? "" : "s"} &middot; ${filled} with entries</div></div>
      </div>
      ${foalInfoBlock(foal, farmName)}
      <table>${colgroup}<thead>${headerTr}</thead><tbody>${bodyRows}</tbody></table>
      ${farrierSection}
      <div class="page-footer"><div class="pf-left"><span class="pf-brand">EQUIMARE PRO</span> &middot; Foal Card &middot; ${escapeHtml(foalName(foal))}</div><div class="pf-right">Generated ${generatedAt}</div></div>
    </section>`;

  return `<!doctype html><html><head><meta charset="utf-8"/><style>${REPORT_CSS}${FOAL_CARD_CSS}</style></head><body>${pageHtml}</body></html>`;
}


// ---------- Foal Registry (portrait roster PDF) ----------
const FOAL_REGISTRY_CSS = `
  @page { size: letter portrait; margin: 0.4in; }
  @page :first { margin: 0.4in; }
  td.reg-name { font-weight:700; color:#000; background:#EDEDED !important; }
  td.reg-dob .age { color:#03023A; font-weight:700; font-variant-numeric:tabular-nums; }
`;

const FOAL_REGISTRY_ROWS_PER_PAGE = 34;

function foalAgeDays(dob) {
  if (!dob) return null;
  const d = new Date(`${dob}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d.getTime()) / 86400000);
}

export function buildFoalRegistryHtml(foals, settings = DEFAULT_SETTINGS, includeDeceased = true) {
  const farmName = (settings.farm_name || "").trim() || printedName(settings) || "";
  const kindLabel = "Foal Registry";
  const heroTitle = (farmName || kindLabel).toUpperCase();
  const heroSub = farmName ? kindLabel.toUpperCase() : "";
  const generatedAt = new Date().toLocaleString();
  const dateBanner = `AS OF ${prettyDateLong(todayYMD()).toUpperCase()}`;

  const sorted = [...(foals || [])]
    .filter((f) => includeDeceased || !f.deceased)
    .sort((a, b) => (a.foal_out_date || "").localeCompare(b.foal_out_date || ""));

  const COLS = [
    { label: "Foal", pct: 24 },
    { label: "Date of Birth (Age)", pct: 26 },
    { label: "Stallion", pct: 20 },
    { label: "Sex", pct: 12 },
    { label: "Breed", pct: 18 },
  ];
  const colgroupHtml = `<colgroup>${COLS.map((c) => `<col style="width:${c.pct}%"/>`).join("")}</colgroup>`;
  const columnHeaderTr = `<tr>${COLS.map((c) => `<th>${c.label.toUpperCase()}</th>`).join("")}</tr>`;

  const dash = `<span style="color:#999">—</span>`;
  const rowHtml = (f) => {
    const nm = escapeHtml(foalName(f));
    const nameCell = f.deceased
      ? `${nm} <span style="font-size:8px;font-weight:700;text-transform:uppercase;letter-spacing:0.4px;color:#8A8A8A;">(Deceased)</span>`
      : nm;
    const dob = f.foal_out_date || "";
    const age = foalAgeDays(dob);
    const dobCell = dob
      ? `${escapeHtml(prettyDate(dob))}${age != null ? ` <span class="age">(${age} day${age === 1 ? "" : "s"})</span>` : ""}`
      : dash;
    return `<tr>
      <td class="reg-name">${nameCell}</td>
      <td class="reg-dob">${dobCell}</td>
      <td>${escapeHtml(f.stallion || "") || dash}</td>
      <td>${escapeHtml(f.sex || "") || dash}</td>
      <td>${escapeHtml(f.breed || "") || dash}</td>
    </tr>`;
  };

  const chunkSize = FOAL_REGISTRY_ROWS_PER_PAGE;
  const chunks =
    sorted.length === 0
      ? [[]]
      : Array.from({ length: Math.ceil(sorted.length / chunkSize) }, (_, i) =>
          sorted.slice(i * chunkSize, i * chunkSize + chunkSize)
        );
  const totalPages = chunks.length;

  const pagesHtml = chunks
    .map((chunk, pageIdx) => {
      const bodyRowsHtml =
        chunk.length === 0
          ? `<tr><td colspan="${COLS.length}" class="empty-row">No foals registered yet.</td></tr>`
          : chunk.map(rowHtml).join("");
      return renderPageSection({
        heroTitle,
        heroSub,
        vetHeader: vetHeaderHtml(settings),
        dateBanner,
        metaCount: `${sorted.length} foal${sorted.length === 1 ? "" : "s"}`,
        colgroupHtml,
        columnHeaderTr,
        bodyRowsHtml,
        footerBrandStrip: `<span class="pf-brand">EQUIMARE PRO</span> &middot; Foal Registry${farmName ? ` &middot; ${escapeHtml(farmName)}` : ""}`,
        footerRight: `Page ${pageIdx + 1} of ${totalPages} &middot; Printed ${generatedAt}`,
      });
    })
    .join("");

  return `<!doctype html><html><head><meta charset="utf-8"/><style>${stripLandscapePage(REPORT_CSS)}${FOAL_REGISTRY_CSS}</style></head><body>${pagesHtml}</body></html>`;
}


// ---------- Bred Dates & Procedures reports (date-range list, landscape) ----------
function buildListReport({ kindLabel, dateBanner, entries, settings, cols, orientation = "landscape", rowClassFor, cellClassFor, rowsPerPage, nameLabel = "Mare" }) {
  const nameHero = escapeHtml(printedName(settings));
  const heroTitle = (nameHero || kindLabel).toUpperCase();
  const heroSub = nameHero ? kindLabel.toUpperCase() : "";
  const generatedAt = new Date().toLocaleString();

  const columnHeaderTr = `<tr><th>#</th><th>${escapeHtml(nameLabel).toUpperCase()}</th>${cols.map((c) => `<th>${c.label.toUpperCase()}</th>`).join("")}</tr>`;
  const reservedIdx = 4;
  const reservedMare = 18;
  const dataPctSum = cols.reduce((s, c) => s + (c.pct ?? 0), 0);
  const slack = Math.max(0, 100 - reservedIdx - reservedMare - dataPctSum);
  const boost = cols.length ? slack / cols.length : 0;
  const colgroupHtml =
    `<colgroup><col style="width:${reservedIdx}%"/><col style="width:${reservedMare}%"/>` +
    cols.map((c) => `<col style="width:${((c.pct ?? 0) + boost).toFixed(2)}%"/>`).join("") +
    `</colgroup>`;

  const DATE_KEYS = ["date", "confirmed_in_foal_date", "estimated_due_date", "last_service_date", "next_check", "check_14", "check_28", "check_45"];
  const cellFor = (r, c) => {
    let raw = r[c.key] ?? "";
    if (DATE_KEYS.includes(c.key) && raw) {
      raw = c.key === "date" || /^\d{4}-\d{2}-\d{2}$/.test(raw) ? prettyDate(raw) : raw;
    }
    if (c.key === "procedures") raw = proceduresText(r);
    if (c.key === "notes") raw = r.notes || "";
    const val = escapeHtml(raw) || "";
    const extra = cellClassFor ? cellClassFor(r, c) : "";
    const cls = [];
    if (DATE_KEYS.includes(c.key)) cls.push("next-check");
    else if (c.key === "notes") cls.push("notes");
    if (extra) cls.push(extra);
    return `<td${cls.length ? ` class="${cls.join(" ")}"` : ""}>${val}</td>`;
  };

  const chunkSize = rowsPerPage || ROWS_PER_PAGE;
  const chunks =
    entries.length === 0
      ? [[]]
      : Array.from({ length: Math.ceil(entries.length / chunkSize) }, (_, i) =>
          entries.slice(i * chunkSize, i * chunkSize + chunkSize)
        );
  const totalPages = chunks.length;

  const pagesHtml = chunks
    .map((chunk, pageIdx) => {
      const startAbsIdx = pageIdx * chunkSize;
      const bodyRowsHtml =
        chunk.length === 0
          ? `<tr><td colspan="${cols.length + 2}" class="empty-row">No entries in this date range.</td></tr>`
          : chunk
              .map(
                (r, i) => {
                  const rc = rowClassFor ? rowClassFor(r) : "";
                  return `<tr${rc ? ` class="${rc}"` : ""}><td class="idx">${startAbsIdx + i + 1}</td><td class="mare">${escapeHtml(r.mare_name)}</td>${cols
                    .map((c) => cellFor(r, c))
                    .join("")}</tr>`;
                }
              )
              .join("");
      return renderPageSection({
        heroTitle,
        heroSub,
        vetHeader: vetHeaderHtml(settings),
        dateBanner,
        metaCount: `${entries.length} entr${entries.length === 1 ? "y" : "ies"}`,
        colgroupHtml,
        columnHeaderTr,
        bodyRowsHtml,
        footerBrandStrip: `<span class="pf-brand">EQUIMARE PRO</span> &middot; ${kindLabel}`,
        footerRight: `Page ${pageIdx + 1} of ${totalPages} &middot; Generated ${generatedAt}`,
      });
    })
    .join("");

  const baseCss = orientation === "portrait" ? stripLandscapePage(REPORT_CSS) : REPORT_CSS;
  const orientationCss = orientation === "portrait" ? "@page { size: letter portrait; margin: 0.3in; }" : "";
  return `<!doctype html><html><head><meta charset="utf-8"/><style>${baseCss}${orientationCss}</style></head><body>${pagesHtml}</body></html>`;
}

export function buildBredReportHtml(entries, settings = DEFAULT_SETTINGS, start, end) {
  return buildListReport({
    kindLabel: "Cover Dates Report",
    dateBanner: `${prettyDate(start)} — ${prettyDate(end)}`.toUpperCase(),
    entries: entries || [],
    settings,
    orientation: "portrait",
    cols: [
      { key: "date", label: "Cover Date", pct: 20 },
      { key: "stallion", label: "Stallion", pct: 26 },
      { key: "estimated_due_date", label: "Est. Due Date", pct: 24 },
    ],
  });
}

export function buildInFoalReportHtml(entries, settings = DEFAULT_SETTINGS) {
  return buildListReport({
    kindLabel: "In-Foal Season Summary",
    dateBanner: "IN FOAL — SEASON SUMMARY",
    entries: entries || [],
    settings,
    orientation: "portrait",
    cols: [
      { key: "last_service_date", label: "Last Cover Date", pct: 22 },
      { key: "stallion", label: "Stallion", pct: 20 },
      { key: "estimated_due_date", label: "Est. Due Date", pct: 22 },
    ],
  });
}

export function buildFarrierReportHtml(entries, settings = DEFAULT_SETTINGS, reportDate) {
  return buildListReport({
    kindLabel: "Farrier List",
    dateBanner: `FARRIER LIST — ${prettyDateLong(reportDate || todayYMD()).toUpperCase()}`,
    entries: entries || [],
    settings,
    nameLabel: "Foal",
    orientation: "portrait",
    cols: [
      { key: "dob", label: "Date of Birth", pct: 15 },
      { key: "age", label: "Age", pct: 10 },
      { key: "last_visit", label: "Last Visit", pct: 13 },
      { key: "last_notes", label: "Last Farrier Notes", pct: 30 },
      { key: "writein", label: "Notes", pct: 32 },
    ],
  });
}

export function buildBreedingScheduleReportHtml(entries, settings = DEFAULT_SETTINGS, asOf) {
  const statusKey = { check_14: "check_14_status", check_28: "check_28_status", check_45: "check_45_status" };
  return buildListReport({
    kindLabel: "Breeding & Ovulation Schedule",
    dateBanner: `BREEDING & OVULATION — AS OF ${prettyDateLong(asOf || todayYMD()).toUpperCase()} · GREY = DONE · GOLD = DUE THIS WEEK · RED = OVERDUE · CHECKS 14/28/45d POST-OV`,
    entries: entries || [],
    settings,
    rowsPerPage: 28,
    cellClassFor: (r, c) => {
      const sk = statusKey[c.key];
      if (!sk) return "";
      if (r[sk] === "done") return "chk-done";
      if (r[sk] === "due") return "chk-due";
      if (r[sk] === "overdue") return "chk-overdue";
      return "";
    },
    cols: [
      { key: "stallion", label: "Stallion", pct: 12 },
      { key: "bred_date", label: "Date Bred", pct: 11 },
      { key: "ov_date", label: "OV Date", pct: 11 },
      { key: "ov_side", label: "OV Side", pct: 7 },
      { key: "check_14", label: "14-Day Check", pct: 11 },
      { key: "check_28", label: "28-Day Check", pct: 11 },
      { key: "check_45", label: "45-Day Check", pct: 11 },
    ],
  });
}

export function buildMareStatusReportHtml(entries, settings = DEFAULT_SETTINGS) {
  return buildListReport({
    kindLabel: "Mare Status Report",
    dateBanner: `MARE STATUS — AS OF ${prettyDateLong(todayYMD()).toUpperCase()} · AMBER = 7+ DAYS OVERDUE · PINK = BRED · GREEN = CONFIRMED IN FOAL`,
    entries: entries || [],
    settings,
    rowClassFor: (r) => (r.status ? `flag-${r.status}` : ""),
    cols: [
      { key: "date", label: "Last Checked", pct: 11 },
      { key: "stallion", label: "Stallion", pct: 12 },
      { key: "left_ovary", label: "L. Ovary", pct: 8 },
      { key: "right_ovary", label: "R. Ovary", pct: 8 },
      { key: "fluid", label: "Fluid", pct: 6 },
      { key: "edema", label: "Edema", pct: 6 },
      { key: "cervix", label: "Cervix", pct: 8 },
      { key: "notes", label: "Notes", pct: 16 },
      { key: "next_check", label: "Next Check", pct: 9 },
    ],
  });
}

export function buildProceduresReportHtml(entries, settings = DEFAULT_SETTINGS, start, end, procedure) {
  return buildListReport({
    kindLabel: procedure ? `${procedure} Report` : "Procedures Report",
    dateBanner: `${prettyDate(start)} — ${prettyDate(end)}`.toUpperCase(),
    entries: entries || [],
    settings,
    cols: [
      { key: "date", label: "Date", pct: 16 },
      { key: "procedures", label: "Procedures", pct: 26 },
      { key: "notes", label: "Notes", pct: 34 },
    ],
  });
}
